import { Contact, Entity } from '@bumble/app/__generated__/data-contracts';
import {
  ContactCreate,
  ContactUpdate
} from '@bumble/app/components/contact';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import { Loading } from '@bumble/app/components/Loading';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonRemoveOutlinedIcon from '@mui/icons-material/PersonRemoveOutlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import { Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import SplitPanes from '../components/SplitPanes';
import BumbleTabs from '../components/BumbleTabs';
import MultipleSelections from '../components/MultipleSelections';
import { ContactDetails } from '../components/contact/ContactDetails';
import { ContactAssignEntity } from '../components/contact/ContactAssignEntity';

const columns: GridColDef[] = [
  {
    field: 'first_name',
    headerName: 'First Name',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'last_name',
    headerName: 'Last Name',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'phone',
    headerName: 'Phone',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'contact_type',
    headerName: 'Type',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'entity_name',
    headerName: 'Entity Name',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 150,
    disableColumnMenu: true,
    valueGetter: (value) => {
      if (value === null) return '';
      return DateTime.fromISO(value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
//  {
//    field: 'updated_at',
//    headerName: 'Updated at',
//    minWidth: 150,
//    headerAlign: 'right',
//    align: 'right',
//    disableColumnMenu: true,
//    valueGetter: (params) => {
//      if (params.value === null) return '';
//      return DateTime.fromISO(params.value).toLocaleString(DateTime.DATETIME_SHORT);
//    },
//  },
];

const columnVisible = {
  first_name: true,
  last_name: true,
  email: true,
  phone: true,
  contact_type: true,
  entity_name: true,
  created_at: true,
  updated_at: false,
};

export const Contacts = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const [selectedGroup, setSelectedGroup] = useState<Contact | null>(null);
  const [showCreateContact, setShowCreateContact] = useState<boolean>(false);
  const [showUpdateContact, setShowUpdateContact] = useState<boolean>(false);
  const [showAssignEntityModal, setShowAssignEntityModal] = useState<boolean>(false);
  const [showRemoveContact, setShowRemoveContact] = useState<boolean>(false);
  const [assignedEntities, setAssignedEntities] = useState<Entity[]>([]);

  const api = useApi();

  const { isLoading, data, refetch } = useQuery(
    ['getContacts'],
    api.getContactApiV1GetContactGet.bind(null, {}),
    { enabled: true },
  );

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed Contacts');
  };

  const selectedRowGroup = useMemo(() => {
    if (rowSelectionModel.length !== 1) return null;
    const itemId = rowSelectionModel[0].toString();
    return data?.data.find((item) => item.contact_id === itemId) || null;
  }, [data, rowSelectionModel]);

  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedGroup(selectedRowGroup);
      getEntities.mutate({ contact_id: selectedRowGroup!.contact_id });
    } else {
      setSelectedGroup(null);
    }
  }, [rowSelectionModel, selectedRowGroup]);

  const handleHideCreateContact = () => {
    handleDataRefetch();
    setShowCreateContact(false);
  };

  const handleHideUpdateContact = () => {
    handleDataRefetch();
    setShowUpdateContact(false);
  };

  const handleHideAssignEntityModal = () => {
    handleDataRefetch();
    setShowAssignEntityModal(false);
  };

  const getEntities = useMutation(api.getContactAssignedEntitiesApiV1GetContactAssignedEntitiesGet, {
    onSuccess: (data) => {
      setAssignedEntities(data?.data);
    },
    onError: apiError,
  });

  const refetchAssignedEntities = () => {
    getEntities.mutate({ contact_id: selectedRowGroup!.contact_id });
  };

  const removeContact = useMutation(api.removeContactApiV1RemoveContactPost, {
    onSuccess() {
      toast.success('Contact removed.');
      handleDataRefetch();
      setRowSelectionModel([]);
    },
    onError: apiError,
  });

  const handleRemoveContact = () => {
    if (selectedGroup) {
      removeContact.mutate({
        contact_id: selectedGroup.contact_id,
      });
    }
  };

  return (
    <PageContent>
      <PageHeader
        pageName="Contacts"
        count={data?.data?.length || 0}
        description="Manage your business and technical support contacts for both App Services and Endpoints. This page provides a centralized location to view all current support contacts. Easily update or remove existing contacts to keep your information accurate. Once you have added a contact, you can go to the App Services or Endpoints page to apply those contacts."
      />
      {isLoading ? (
        <Loading />
      ) : (
        <SplitPanes top = {
          <Card elevation={0}>
            <CardContent>
              <DataTableGrid
                rows={
                  data?.data.map((item) => ({
                    ...item,
                    id: item.contact_id,
                  })) || []
                }
                columns={columns}
                handleSelectionChange={handleSelectionChange}
                rowSelectionModel={rowSelectionModel}
                itemsSelected={rowSelectionModel.length === 1}
                columnVisibilityModel={columnVisible}
                refreshData={handleDataRefetch}
                newLabel="Add Contact"
                newOnClick={() => setShowCreateContact(true)}
                menuItems={[
                  {
                    label: 'Update Contact',
                    icon: <ManageAccountsOutlinedIcon color="info" fontSize="small" />,
                    onClick: () => setShowUpdateContact(true),
                  },
                  {
                    label: 'Assign Entity',
                    icon: <AddLinkIcon color="info" fontSize="small" />,
                    onClick: () => setShowAssignEntityModal(true),
                  },
                  {
                    label: 'Remove Contact',
                    icon: <PersonRemoveOutlinedIcon color="error" fontSize="small" />,
                    onClick: () => setShowRemoveContact(true),
                    hasDivider: true,
                  },
                ]}
              />
            </CardContent>
          </Card>
        } bottom = {
          <Card elevation={0} sx={{ mt: 5 }}>
            <CardContent>
              {rowSelectionModel.length === 1 ? (
                <BumbleTabs
                  tabs={[{
                    el: <ContactDetails
                          data={assignedEntities}
                          contactId={selectedGroup?.contact_id}
                          refetchData={refetchAssignedEntities}
                        />,
                    label: 'Details',
                    id: 0,
                  }]}
                />
              ) : (
                <MultipleSelections count={rowSelectionModel.length} />
              )}
            </CardContent>
          </Card>
        }>
        </SplitPanes>
      )}
      {showCreateContact && (
        <CreateModal
          open={showCreateContact}
          handleClose={() => setShowCreateContact(false)}
          title="Add Contact"
        >
          <ContactCreate onSubmit={handleHideCreateContact} />
        </CreateModal>
      )}
      {showUpdateContact && selectedGroup && (
        <DialogStandard
          open={showUpdateContact}
          handleClose={() => setShowUpdateContact(false)}
          title="Update Contact"
        >
          <ContactUpdate
            onSubmit={handleHideUpdateContact}
            contact={selectedGroup}
          />
        </DialogStandard>
      )}
      {showAssignEntityModal && selectedGroup && (
        <DialogStandard
          open={showAssignEntityModal}
          handleClose={() => setShowAssignEntityModal(false)}
          title="Assign Entity"
        >
          <ContactAssignEntity
            onSubmit={handleHideAssignEntityModal}
            contactId={selectedGroup.contact_id}
          />
        </DialogStandard>
      )}
      {showRemoveContact && selectedGroup && (
        <DialogConfirm
          open={showRemoveContact}
          handleClose={() => setShowRemoveContact(false)}
          handleConfirm={handleRemoveContact}
          title="Remove Contact?"
          description="Are you certain you wish to proceed with deleting this contact?"
          confirmationPhrase="confirm"
        />
      )}
    </PageContent>
  );
};
